import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/DataTableContainer"
import DashboardLoader from "components/Loader/DashboardLoader"
import DeleteModal from "pages/Calendar/DeleteModal"
import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  useGetAllCostingsQuery,
  useDeleteCostingMutation,
} from "store/features/costing/costingManagementApi"
import EditCostingModal from "components/Modals/EditCostingModal"

const AllCostings = () => {
  const { data: costings, isLoading, refetch } = useGetAllCostingsQuery()
  const [deleteCosting, { isLoading: deletingCosting }] =
    useDeleteCostingMutation()

  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedCostingId, setSelectedCostingId] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedCosting, setSelectedCosting] = useState(null)

  const showDeleteModal = id => {
    setSelectedCostingId(id)
    setDeleteModal(true)
  }

  const handleDeleteCosting = () => {
    deleteCosting(selectedCostingId)
    setDeleteModal(false)
  }

  const handleEditCosting = costing => {
    setSelectedCosting(costing)
    setEditModalOpen(true)
  }

  const handleCostingUpdate = () => {
    refetch()
    setEditModalOpen(false)
  }

  const columns = useMemo(
    () => [
      {
        header: "Project Name",
        accessorKey: "project_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="fw-semibold text-danger">
            ${Number(parseFloat(row.original.amount || 0).toFixed(2)).toLocaleString()}
          </div>
        ),
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.notes || "No notes",
      },
      {
        header: "Date",
        accessorKey: "datesystem",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          if (!row.original.datesystem) return "N/A"
          const date = new Date(row.original.datesystem)
          return date.toLocaleDateString()
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={() => handleEditCosting(row.original)}
              className="btn btn-primary btn-sm"
            >
              <i className="fas fa-edit me-1"></i> Edit
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
            >
              <i className="fas fa-trash me-1"></i>
              {deletingCosting ? "Deleting..." : "Delete"}
            </button>
          </div>
        ),
      },
    ],
    [deletingCosting]
  )

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Costings" breadcrumbItem="All Costings" />

        <div className="mb-3">
          <Link to="/costings/add" className="btn btn-primary">
            <i className="fas fa-plus me-2"></i>Add New Costing
          </Link>
        </div>

        <TableContainer
          columns={columns}
          data={costings || []}
          isGlobalFilter={true}
          isPagination={true}
          SearchPlaceholder="Search Costings..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteCosting}
          loader={deletingCosting}
          onCloseClick={() => setDeleteModal(false)}
        />

        {selectedCosting && (
          <EditCostingModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            costing={selectedCosting}
            onCostingUpdate={handleCostingUpdate}
          />
        )}
      </div>
    </div>
  )
}

export default AllCostings
